import { useCallback } from 'react';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FieldsetCard } from '@/components/atoms/FieldsetCard/FieldsetCard';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useBilling } from '@/modules/billing/hooks/useBilling';
import { isRequired } from '@/util/validation/constants';

type FormValues = { name: string };
const FORM_ID = 'company-name-form';

export const CompanyNameFieldset = () => {
  const { t } = useTranslation();
  const { customer, updateCustomer, updateCustomerStatus } = useBilling();

  const form = useForm<FormValues>({ mode: 'onChange', values: { name: customer?.name ?? '' } });
  const {
    handleSubmit,
    control,
    formState: { isDirty },
  } = form;

  const onSubmit = useCallback(
    ({ name }: FormValues) => updateCustomer({ name }),
    [updateCustomer]
  );

  return (
    <div className="flex flex-col gap-2">
      <h2 className="px-1.5 text-sm font-medium text-foreground-muted">
        {t('billing.company_name')}
      </h2>
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID}>
          <FieldsetCard
            isDirty={isDirty}
            isLoading={updateCustomerStatus === 'pending'}
            disabled={!isDirty}
            saveButtonLabel={t('billing.company_change_name')}
            formId={FORM_ID}
          >
            <FormField
              control={control}
              name="name"
              rules={isRequired}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('field.name')}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormDescription>{t('billing.company_name_subtitle')}</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FieldsetCard>
        </form>
      </Form>
    </div>
  );
};
